import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../store/hooks";
import { signOut, updateAccessToken } from "../../store/slices/authSlice";

// pages that are public as a whole page, matched exactly (a prefix match
// would also open everything nested under them)
const PUBLIC_EXACT_PATHS = [
  "/",
  // the Art of the Day is viewable signed out; only comments need an account
  "/art-of-the-day",
  // the game's join screen is viewable signed out; joining needs an account
  "/game",
];

// pages that don't require authentication - don't redirect from these
const PUBLIC_PATHS = [
  "/login",
  "/register",
  "/reset-pw",
  "/forgot-pw",
  "/auth/callback",
  "/auth/",
  "/oauth",
  "/unsubscribe",
  "/about",
  "/contact",
  "/privacy",
  "/cookies",
  "/terms",
  // poster and card QR codes: record the scan, then today's prints
  "/aotd/",
  // AOTD prints, browsable signed out
  "/collectibles",
  "/art-of-the-day/today/prints",
  "/labs/",
  // the public badge share page (a logged-out visitor from a story link)
  "/badge/s/",
];

/**
 * session manager component - listens for session events and handles them app-wide
 * 
 * events handled:
 * - session-expired: when refresh token fails or expires
 * - token-refreshed: when token is successfully refreshed (silent)
 * 
 * mount this component at the root of your app (e.g., in App.tsx)
 */
export const SessionManager: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigateRef = useRef(navigate);
  const dispatchRef = useRef(dispatch);
  const redirectingRef = useRef(false);

  // keep refs up to date
  useEffect(() => {
    navigateRef.current = navigate;
    dispatchRef.current = dispatch;
  }, [navigate, dispatch]);

  useEffect(() => {
    console.log("[SessionManager] Setting up event listeners");
    
    // handle session expiry
    const handleSessionExpired = (event: Event) => {
      const customEvent = event as CustomEvent<{ reason?: string; error?: unknown }>;
      const { reason } = customEvent.detail || {};

      console.log("[SessionManager] Session expired event received, reason:", reason);

      // always clear Redux state to prevent redirect loops
      dispatchRef.current(signOut());

      // check if we're on a public page - don't redirect
      const currentPath = window.location.pathname;
      const isPublicPage =
        PUBLIC_EXACT_PATHS.includes(currentPath.replace(/\/+$/, "") || "/") ||
        PUBLIC_PATHS.some(path => currentPath.startsWith(path));

      console.log("[SessionManager] Current path:", currentPath, "isPublicPage:", isPublicPage);

      if (isPublicPage) {
        console.log("[SessionManager] On public page, not redirecting");
        return;
      }

      // only show toast if not a manual logout
      if (reason !== "logout") {
        toast.error("Your session has expired. Please log in again.", {
          autoClose: 5000,
        });
      }

      // several in-flight requests can each fail and fire session-expired, and
      // each one used to trigger its own navigation. one is enough.
      if (redirectingRef.current) {
        console.log("[SessionManager] Already redirecting, ignoring duplicate event");
        return;
      }
      redirectingRef.current = true;
      window.setTimeout(() => {
        redirectingRef.current = false;
      }, 5000);

      // redirect to login with return URL
      const returnUrl = encodeURIComponent(currentPath + window.location.search);
      const loginPath = `/login?return=${returnUrl}`;
      console.log("[SessionManager] Redirecting to:", loginPath);
      
      try {
        navigateRef.current(loginPath, { replace: true });
        console.log("[SessionManager] Navigate called successfully");
      } catch (error) {
        console.error("[SessionManager] Error during navigation:", error);
        // fallback to window.location if navigate fails
        window.location.href = loginPath;
      }
    };

    // handle successful token refresh (optional: can be used for analytics)
    const handleTokenRefreshed = (event: Event) => {
      const customEvent = event as CustomEvent<{ accessToken?: string }>;
      const nextAccessToken = customEvent.detail?.accessToken;
      if (nextAccessToken) {
        dispatchRef.current(updateAccessToken(nextAccessToken));
      }
      console.log("[SessionManager] Token refreshed successfully");
    };

    // add event listeners
    window.addEventListener("session-expired", handleSessionExpired);
    window.addEventListener("token-refreshed", handleTokenRefreshed);
    console.log("[SessionManager] Event listeners registered");

    // cleanup
    return () => {
      console.log("[SessionManager] Cleaning up event listeners");
      window.removeEventListener("session-expired", handleSessionExpired);
      window.removeEventListener("token-refreshed", handleTokenRefreshed);
    };
  }, []); // empty deps - use refs for navigate and dispatch

  // this component doesn't render anything
  return null;
};
